import { useEffect, useMemo, useState } from 'react';
import { Save, Trash2, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { TextField, Checkbox, Autocomplete } from '@anyemp/ui-kit';
import ConfirmDialog from '../UI/ConfirmDialog';
import { statuses as statusesApi } from '../../services/apiService';
import { dateTypes } from '../../utils/dateTypeUtils';

const TALENT_TYPE_OPTIONS = [
  { value: '', label: 'Any talent type' },
  { value: 'candidate', label: 'Candidate' },
  { value: 'trainee', label: 'Trainee' },
  { value: 'employee', label: 'Employee' },
  { value: 'presale', label: 'Presale' },
];

const TRIGGER_TYPE_OPTIONS = [
  { value: 'manual_only', label: 'Manual only — started by user' },
  { value: 'status_change', label: 'Auto on status change' },
  { value: 'date_reached', label: 'Auto when a date is reached' },
];

const ANCHOR_DATE_OPTIONS = [
  { value: 'started_at', label: 'Instance start date (default)' },
  ...dateTypes.map((dt) => ({ value: dt.value, label: `ImportantDate: ${dt.label}` })),
];

const DATE_TYPE_OPTIONS = dateTypes.map((dt) => ({ value: dt.value, label: dt.label }));

const buildDraft = (playbook) => ({
  name: playbook?.name || '',
  talent_type: playbook?.talent_type || '',
  description: playbook?.description || '',
  trigger_type: playbook?.trigger_type || 'manual_only',
  trigger_status_id: playbook?.trigger_status_id != null ? String(playbook.trigger_status_id) : null,
  trigger_date_type: playbook?.trigger_date_type || '',
  trigger_date_offset_days: playbook?.trigger_date_offset_days ?? 0,
  anchor_date_type: playbook?.anchor_date_type || 'started_at',
  default_duration_days: playbook?.default_duration_days ?? 90,
  is_active: playbook?.is_active !== false,
});

const PlaybookForm = ({ playbook, canEdit, onSave, onDeactivate, isNew = false }) => {
  const [draft, setDraft] = useState(() => buildDraft(playbook));
  const [statuses, setStatuses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  useEffect(() => {
    setDraft(buildDraft(playbook));
  }, [playbook?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load statuses for the chosen talent_type (or all if "any")
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = draft.talent_type
          ? await statusesApi.getStatusesByType(draft.talent_type, { isCommon: 1 })
          : await statusesApi.getAllStatuses({ isCommon: 1 });
        if (!cancelled) setStatuses(res?.data || []);
      } catch {
        if (!cancelled) setStatuses([]);
      }
    })();
    return () => { cancelled = true; };
  }, [draft.talent_type]);

  const statusOptions = useMemo(
    () => statuses.map((s) => ({ value: String(s.id), label: s.name })),
    [statuses]
  );

  const update = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const handleSave = async () => {
    if (!draft.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    if (draft.trigger_type === 'status_change' && !draft.trigger_status_id) {
      toast.error('Pick a status for "Auto on status change"');
      return;
    }
    if (draft.trigger_type === 'date_reached' && !draft.trigger_date_type) {
      toast.error('Pick a date type for "Auto when a date is reached"');
      return;
    }

    const payload = {
      ...draft,
      talent_type: draft.talent_type || null,
      trigger_status_id: draft.trigger_type === 'status_change' && draft.trigger_status_id ? parseInt(draft.trigger_status_id) : null,
      trigger_date_type: draft.trigger_type === 'date_reached' ? draft.trigger_date_type : null,
      trigger_date_offset_days: draft.trigger_type === 'date_reached' ? Number(draft.trigger_date_offset_days) || 0 : 0,
      default_duration_days: Number(draft.default_duration_days) || 1,
    };

    setSaving(true);
    try {
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  const showStatusPicker = draft.trigger_type === 'status_change';
  const showDatePicker = draft.trigger_type === 'date_reached';

  return (
    <div className="space-y-4">
      {isNew && (
        <div className="flex gap-2 p-3 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-900 dark:text-blue-200 leading-relaxed">
            A <b>playbook</b> is a reusable template of stage steps (e.g. Onboarding has Welcome → HR docs → Training → 1-on-1 → Review).
            When started for a talent, it creates an <i>instance</i> with concrete due-dates and shows up on the talent's profile (Stages tab) and in the calendar.
            Save the playbook first, then add steps below.
          </div>
        </div>
      )}

      <div className="flex flex-col items-stretch gap-3 border-b border-light-border-primary pb-3 dark:border-dark-border-primary sm:flex-row sm:items-center sm:justify-between">
        <h2 className="min-w-0 break-words text-base font-semibold text-light-text-primary dark:text-dark-text-primary">
          {isNew ? 'New Playbook' : draft.name || 'Untitled'}
        </h2>
        {canEdit && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {!isNew && playbook?.is_active && (
              <button
                onClick={() => setConfirmDeactivate(true)}
                className="inline-flex min-h-10 items-center gap-1 px-3 py-2 text-xs border border-light-border-primary dark:border-dark-border-primary rounded text-light-text-secondary dark:text-dark-text-secondary hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
              >
                <Trash2 className="h-3 w-3" /> Deactivate
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex min-h-10 items-center gap-1 px-3 py-2 text-xs bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white rounded disabled:opacity-50"
            >
              <Save className="h-3.5 w-3.5" /> {saving ? 'Saving...' : isNew ? 'Create' : 'Save'}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <TextField
            label="Name"
            required
            size="small"
            fullWidth
            type="text"
            value={draft.name}
            onChange={(e) => update({ name: e.target.value })}
            disabled={!canEdit}
            helperText={'Shown in the "Start Playbook" dropdown on a talent\'s profile. Must be unique. Examples: "Onboarding", "Probation Review", "Off-boarding".'}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Talent type</label>
          <Autocomplete
            fullWidth
            value={draft.talent_type}
            onChange={(value) => update({ talent_type: value || '' })}
            options={TALENT_TYPE_OPTIONS}
            disabled={!canEdit}
          />
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-1">
            Restricts the playbook to one talent type. "Any" makes it available for every talent. Affects which talents can start it and which statuses are listed below.
          </p>
        </div>
      </div>

      <div>
        <TextField
          label="Description"
          multiline
          rows={2}
          size="small"
          fullWidth
          value={draft.description}
          onChange={(e) => update({ description: e.target.value })}
          disabled={!canEdit}
          helperText="Free-form text shown to recruiters in the Start Playbook modal — helps them pick the right one."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Trigger</label>
          <Autocomplete
            fullWidth
            value={draft.trigger_type}
            onChange={(value) => update({ trigger_type: value || '' })}
            options={TRIGGER_TYPE_OPTIONS}
            disabled={!canEdit}
          />
          <div className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-1 space-y-0.5">
            <div>How instances are created:</div>
            <div><b>Manual</b> — recruiter clicks "Start Playbook" on the profile.</div>
            <div><b>Status change</b> — starts automatically when a talent moves into a chosen status.</div>
            <div><b>Date reached</b> — a daily cron checks all talents and starts when a chosen ImportantDate becomes today.</div>
          </div>
        </div>
        <div>
          <TextField
            label="Default duration (days)"
            size="small"
            fullWidth
            type="number"
            min={1}
            value={draft.default_duration_days}
            onChange={(e) => update({ default_duration_days: e.target.value })}
            disabled={!canEdit}
            helperText="How long the stage lasts by default. When an instance is created, ends_at = started_at + N days. The recruiter can override this in the Start Playbook modal."
          />
        </div>
      </div>

      {showStatusPicker && (
        <div>
          <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Auto-start when status changes to</label>
          <Autocomplete
            clearable
            fullWidth
            value={draft.trigger_status_id}
            onChange={(value) => update({ trigger_status_id: value || null })}
            options={statusOptions}
            placeholder={statusOptions.length === 0 ? 'No statuses available' : 'Pick a status'}
            disabled={!canEdit || statusOptions.length === 0}
          />
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-1">
            Every time a talent's status changes to this value, a new instance is created — unless one is already active for this talent + playbook. Re-runs are allowed after the previous instance is completed or cancelled.
          </p>
          {draft.talent_type && statusOptions.length === 0 && (
            <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">No common statuses for {draft.talent_type}</p>
          )}
        </div>
      )}

      {showDatePicker && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_140px] gap-3">
            <div>
              <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Auto-start by date type</label>
              <Autocomplete
                clearable
                fullWidth
                value={draft.trigger_date_type}
                onChange={(value) => update({ trigger_date_type: value || '' })}
                options={DATE_TYPE_OPTIONS}
                placeholder="Pick date type"
                disabled={!canEdit}
              />
            </div>
            <div>
              <TextField
                label="Offset days"
                size="small"
                fullWidth
                type="number"
                value={draft.trigger_date_offset_days}
                onChange={(e) => update({ trigger_date_offset_days: e.target.value })}
                disabled={!canEdit}
              />
            </div>
          </div>
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary -mt-2">
            A daily cron checks: for every talent with an ImportantDate of this type, if today equals date + offset, the playbook starts. Offset can be negative — e.g. -7 means "start a week before the date".
          </p>
        </>
      )}

      <div>
        <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Anchor date for step offsets</label>
        <Autocomplete
          fullWidth
          value={draft.anchor_date_type}
          onChange={(value) => update({ anchor_date_type: value || '' })}
          options={ANCHOR_DATE_OPTIONS}
          disabled={!canEdit}
        />
        <div className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-1 space-y-0.5">
          <div>Each step has an offset in days (e.g. Day +3, Day +30). This setting defines what those offsets are counted from.</div>
          <div><b>Instance start date</b> — counted from started_at of the instance (most common).</div>
          <div><b>ImportantDate</b> — counted from a specific date stored on the talent. For example, anchor on "Start work" so probation steps stay tied to the real start day even if the playbook was launched later.</div>
        </div>
      </div>

      <div>
        <Checkbox
          label="Active"
          color="primary"
          size="small"
          checked={!!draft.is_active}
          onChange={(checked) => update({ is_active: checked })}
          isDisabled={!canEdit}
        />
        <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-1 ml-6">
          When off, this playbook is hidden from the "Start Playbook" dropdown and auto-triggers stop firing. Already-running instances keep going untouched.
        </p>
      </div>

      <ConfirmDialog
        isOpen={confirmDeactivate}
        onClose={() => setConfirmDeactivate(false)}
        onConfirm={() => { setConfirmDeactivate(false); onDeactivate(); }}
        title={`Deactivate "${playbook?.name}"?`}
        message="Existing running instances won't be affected. New ones can't be started until you reactivate."
        confirmText="Deactivate"
        variant="danger"
      />
    </div>
  );
};

export default PlaybookForm;
